// @ts-nocheck
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Context from '../../context/Context';
import { URL } from '../../service/Service';
import { editFormElement } from '../../context/forms/FormsAction';
import Header from '../TitleAndSubtitle';

const TitleEdit = ({ elementInfo }) => {
    const { idform, idformelement } = useParams();
    const { dispatch } = useContext(Context);
    const navigate = useNavigate();
    const location = useLocation();

    const [label, setLabel] = useState(elementInfo?.label ?? '');
    const [placeholder, setPlaceholder] = useState(elementInfo?.placeholder ?? '');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');

    const isAdmin = location.pathname.startsWith('/admin/forms/');
    const baseFormPath = isAdmin ? `/admin/forms/${idform}` : `/forms/${idform}`;

    useEffect(() => {
        setLabel(elementInfo?.label ?? '');
        setPlaceholder(elementInfo?.placeholder ?? '');
    }, [elementInfo?.label, elementInfo?.placeholder]);

    const hasChanges = useMemo(() => {
        return (
            label !== (elementInfo?.label ?? '') ||
            placeholder !== (elementInfo?.placeholder ?? '')
        );
    }, [label, placeholder, elementInfo?.label, elementInfo?.placeholder]);

    const canSave = label.trim().length > 0 && !saving;

    const goBackToEditor = () => {
        navigate(`${baseFormPath}/formwizard/1`);
    };

    const updateHandler = async () => {
        const url = `${URL}/api/forms/${idform}/formelements/${idformelement}`;

        const request = {
            method: 'PATCH',
            headers: {
                'Content-type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                label: label.trim(),
                placeholder: placeholder.trim(),
            }),
        };

        await Promise.resolve(editFormElement(url, request, dispatch));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setError('');
        setSaved(false);

        if (!label.trim()) {
            setError('O título é obrigatório.');
            return;
        }

        try {
            setSaving(true);
            await updateHandler();

            setSaved(true);

            setTimeout(() => {
                navigate(`${baseFormPath}/formwizard/1`);
            }, 700);
        } catch (err) {
            console.error(err);
            setError('Não foi possível guardar o elemento. Tenta novamente.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 px-5 py-6 text-slate-900">
            <div className="mx-auto max-w-7xl">
                <Header
                    eyebrow="Campo de título"
                    title="Editar título do formulário"
                    description="Define o título principal e o subtítulo que aparecem no formulário público."
                    actions={
                        <>
                            <button
                                type="button"
                                onClick={goBackToEditor}
                                className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
                            >
                                Cancelar
                            </button>

                            <button
                                type="submit"
                                form="title-edit-form"
                                disabled={!canSave}
                                className="rounded-xl bg-green-cl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:bg-slate-400"
                            >
                                {saving ? 'A guardar...' : 'Guardar alterações'}
                            </button>
                        </>
                    }
                />

                {error && (
                    <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
                        {error}
                    </div>
                )}

                {saved && (
                    <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-semibold text-emerald-700">
                        Elemento guardado com sucesso.
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
                    <form
                        id="title-edit-form"
                        onSubmit={handleSubmit}
                        className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
                    >
                        <div className="border-b border-slate-100 px-6 py-5">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                                Conteúdo
                            </p>

                            <h2 className="mt-1 text-lg font-bold text-slate-950">
                                Texto do título
                            </h2>

                            <p className="mt-1 text-sm text-slate-500">
                                Este bloco serve para criar separadores, introduções ou cabeçalhos dentro do formulário.
                            </p>
                        </div>

                        <div className="space-y-6 px-6 py-6">
                            <div>
                                <div className="flex items-center justify-between gap-4">
                                    <label
                                        htmlFor="title-label"
                                        className="block text-sm font-bold text-slate-800"
                                    >
                                        Título *
                                    </label>

                                    <span className="text-xs text-slate-400">
                                        {label.length}/120
                                    </span>
                                </div>

                                <input
                                    id="title-label"
                                    type="text"
                                    value={label}
                                    maxLength={120}
                                    onChange={(event) => setLabel(event.target.value)}
                                    placeholder="Ex.: Inscrição no workshop"
                                    className="mt-2 block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                                />

                                <p className="mt-2 text-xs text-slate-500">
                                    Usa uma frase curta e clara. Este texto aparece em destaque.
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center justify-between gap-4">
                                    <label
                                        htmlFor="title-placeholder"
                                        className="block text-sm font-bold text-slate-800"
                                    >
                                        Subtítulo
                                    </label>

                                    <span className="text-xs text-slate-400">
                                        {placeholder.length}/180
                                    </span>
                                </div>

                                <input
                                    id="title-placeholder"
                                    type="text"
                                    value={placeholder}
                                    maxLength={180}
                                    onChange={(event) => setPlaceholder(event.target.value)}
                                    placeholder="Ex.: Preenche os dados abaixo para reservar o teu lugar."
                                    className="mt-2 block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                                />

                                <p className="mt-2 text-xs text-slate-500">
                                    Opcional. Ajuda o utilizador a perceber o que deve fazer nesta secção.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-xs text-slate-500">
                                {hasChanges ? (
                                    <span className="font-semibold text-amber-700">
                                        Existem alterações por guardar.
                                    </span>
                                ) : (
                                    <span>
                                        Sem alterações pendentes.
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col gap-2 sm:flex-row">
                                <button
                                    type="button"
                                    onClick={goBackToEditor}
                                    className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
                                >
                                    Voltar
                                </button>

                                <button
                                    type="submit"
                                    disabled={!canSave}
                                    className="rounded-xl bg-green-cl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:bg-slate-400"
                                >
                                    {saving ? 'A guardar...' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    </form>

                    <aside className="space-y-5">
                        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">
                                Pré-visualização
                            </p>

                            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-6">
                                <h3 className="text-2xl font-bold text-slate-950">
                                    {label.trim() || 'Título da secção'}
                                </h3>

                                <p className="mt-2 text-sm leading-6 text-slate-500">
                                    {placeholder.trim() || 'O subtítulo aparece aqui para orientar o utilizador.'}
                                </p>
                            </div>
                        </section>

                        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="font-bold text-slate-950">
                                Boas práticas
                            </h3>

                            <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                                <li className="rounded-2xl bg-slate-50 px-4 py-3">
                                    Usa títulos curtos e objetivos.
                                </li>

                                <li className="rounded-2xl bg-slate-50 px-4 py-3">
                                    O subtítulo deve explicar o que o utilizador deve fazer.
                                </li>

                                <li className="rounded-2xl bg-slate-50 px-4 py-3">
                                    Evita repetir texto que já aparece noutros campos.
                                </li>
                            </ul>
                        </section>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default TitleEdit;